// src/context/AuthContext.js
//
// Autenticación del POS Compacto contra /api/pos/auth/*.
// Mantiene la MISMA API pública que la versión mock para no romper
// las páginas existentes (user, loading, login, logout, hasPermission).
//
// Mapeo de entidades del backend a roles del POS (mockRoles temporal
// hasta que C10 conecte /api/pos/permisos/*):
//   SUPERADMIN     → rol POS 'Superadmin'   (permisos '*')
//   ADMIN_NEGOCIO  → rol POS 'Admin'        (el tendero dueño de la sucursal)
//   DOMICILIARIO   → rol POS 'Domiciliario'
//
// Los roles 'Cajero' y 'Vendedor' del POS son empleados de una sucursal
// con perfil de permisos, no roles de identidad. Se conectan en C10.

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { auth as authApi } from '../api/authApi';
import { tokenStore } from '../api/apiClient';
import { mockRoles } from '../mock/data';

const AuthContext = createContext(null);

// Mapa entidad_tipo del JWT → rol POS mock (id de mockRoles).
// Preservamos mockRoles porque el resto del POS aún los usa como fuente
// de permisos. Cuando se conecte el sistema real de permisos (C10),
// este mapa se reemplaza por la respuesta del backend.
//
// OJO: el backend devuelve 'NEGOCIO' (no 'ADMIN_NEGOCIO'). El nombre
// 'ADMIN_NEGOCIO' es el rol Identity interno; lo que viaja en el JWT
// como entidad_tipo es 'NEGOCIO'.
//
// Roles que entran al POS por identidad:
//   - SUPERADMIN: para soporte y monitoreo desde Redeprog.
//   - NEGOCIO:    el tendero dueño de la sucursal.
//
// Los empleados del POS (Cajero, Vendedor, Repartidor de la tienda) se
// activan en C10 como perfiles de permisos sobre saas.UsuariosTenant.
// Cuidado: el rol 'Domiciliario' del mockRoles NO es el DOMICILIARIO
// del ecosistema externo — son cosas distintas y no se cruzan acá.
const ENTIDAD_TIPO_A_ROL_ID = {
  SUPERADMIN: 1, // Superadmin (todos los permisos)
  NEGOCIO:    2, // Admin (el tendero dueño de la sucursal)
};

const STORAGE_USER = 'pos_user';

export const AuthProvider = ({ children }) => {
  const [user, setUser]       = useState(null);
  const [loading, setLoading] = useState(true);

  // Restaurar sesión al montar
  useEffect(() => {
    const savedUser  = localStorage.getItem(STORAGE_USER);
    const savedToken = tokenStore.get();
    if (savedUser && savedToken) {
      try { setUser(JSON.parse(savedUser)); }
      catch { localStorage.removeItem(STORAGE_USER); tokenStore.clear(); }
    }
    setLoading(false);
  }, []);

  // Escucha expiración de token (401)
  useEffect(() => {
    const handler = () => {
      localStorage.removeItem(STORAGE_USER);
      setUser(null);
    };
    window.addEventListener('pos-auth-expired', handler);
    return () => window.removeEventListener('pos-auth-expired', handler);
  }, []);

  /**
   * Login real. Recibe correo (con @) o teléfono (con o sin +57) + password.
   * Guarda JWT + info del usuario.
   */
  const login = useCallback(async (usuario, password) => {
    try {
      const res = await authApi.login(usuario, password);
      const entidadTipo = res.entidadTipo || res.EntidadTipo;
      const rolId       = ENTIDAD_TIPO_A_ROL_ID[entidadTipo];

      if (!rolId) {
        return {
          ok: false,
          error: `Este usuario tiene rol '${entidadTipo}' que no puede usar el POS. ` +
                 'El POS es solo para el dueño del negocio y sus empleados de tienda.',
        };
      }

      const rol = mockRoles.find(r => r.id === rolId);
      const userData = {
        id:            res.entidadId || 0,
        aspNetUserId:  res.aspNetUserId || null,
        usuario:       res.correo || usuario,
        nombre:        res.nombre || res.correo || usuario,
        correo:        res.correo || '',
        telefono:      res.telefono || '',
        entidadTipo,
        entidadId:     res.entidadId,
        // BR2: el backend devuelve empresaId cuando es NEGOCIO. El POS lo
        // usa para listar todas las sucursales de la empresa. HolaVeci
        // mobile ignora este campo.
        empresaId:     res.empresaId ?? res.EmpresaId ?? null,
        tenantId:      res.tenantId ?? null,
        rolId,
        rol,
        activo:        true,
      };

      tokenStore.set(res.token || res.Token);
      localStorage.setItem(STORAGE_USER, JSON.stringify(userData));
      setUser(userData);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: e.mensajeUsuario || 'Credenciales inválidas.' };
    }
  }, []);

  const logout = useCallback(() => {
    tokenStore.clear();
    localStorage.removeItem(STORAGE_USER);
    setUser(null);
  }, []);

  const hasPermission = useCallback((perm) => {
    if (!user) return false;
    if (user.rol?.permisos?.includes('*')) return true;
    return user.rol?.permisos?.includes(perm);
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, hasPermission }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
